import { atom, createStore } from 'jotai';
import { debounce, Sentry } from '@idk-web/core-utils';
import { LocalStorage } from '@idk-web/core-ui';
import {
  portalFilterState,
  setPortalFilterState,
  type PortalFilterState,
} from '@/state/portalFilter';
import {
  portalAuthState,
  setPortalAuthState,
  type PortalAuthState,
} from '@/state/portalAuth';
import { localeState, setLocaleState, type LocaleState } from '@/state/locale';

const STORAGE_KEY = 'idk-portal-state-2026-04-22';
const PERSIST_INTERVAL_MS = 10000;

export const store = createStore();

type RootState = {
  locale: LocaleState;
  portalAuth: PortalAuthState;
  portalFilter: PortalFilterState;
};

const state = atom<RootState>((get) => ({
  locale: get(localeState),
  portalAuth: get(portalAuthState),
  portalFilter: get(portalFilterState),
}));

function readState(): RootState | undefined {
  const state = LocalStorage.getItem(STORAGE_KEY);

  try {
    const parsedState = state ? (JSON.parse(state) as RootState) : undefined;

    // Quickfix for companies which is missing a country array for some reason
    if (
      parsedState?.portalAuth.currentCompany &&
      !parsedState.portalAuth.currentCompany.countries
    ) {
      return undefined;
    }

    return parsedState;
  } catch (e: unknown) {
    Sentry.reportError('Failed to parse portal state', { error: e });
    return undefined;
  }
}

function loadState(state: Partial<RootState>) {
  if (state.locale) {
    setLocaleState(state.locale);
  }

  if (state.portalFilter) {
    setPortalFilterState(state.portalFilter);
  }

  if (state.portalAuth) {
    setPortalAuthState(state.portalAuth);
  }
}

function persistState(state: RootState) {
  LocalStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

const persistedState = readState();

if (persistedState) {
  loadState(persistedState);
}

const persist = debounce(() => {
  persistState(store.get(state));
}, PERSIST_INTERVAL_MS);

store.sub(state, persist);
